/**
 * product-recommendations.js — lazy recommendations custom element
 *
 * Wraps the <product-recommendations> custom element that fetches related or
 * complementary products via Shopify's native Recommendations API and injects
 * them into the section shell on scroll-visible.
 *
 * Data attributes (set by the Liquid section):
 *   data-product-id   — current product ID (required)
 *   data-section-id   — the rendered section ID (required)
 *   data-intent       — 'related' | 'complementary' | 'frequently_bought_with'
 *   data-limit        — max products to fetch (default 4)
 *
 * Behavior:
 *   1. IntersectionObserver defers the fetch until the element is near-visible.
 *   2. Fetches `/recommendations/products?section_id=X&product_id=Y&intent=Z&limit=N`.
 *      Shopify returns the fully rendered section HTML for that section_id.
 *   3. Parses the HTML via DOMParser (no innerHTML, no script re-exec), finds
 *      the matching <product-recommendations id="..."> node, and adopts its
 *      children into the live element via replaceChildren(...adoptedNodes).
 *   4. If no products are returned (empty grid), the section hides itself.
 *   5. Errors fail silently (hidden section) — never break the page.
 *
 * Import name: @theme/product-recommendations
 */

import { Component } from '@theme/component';

const ROOT_MARGIN = '0px 0px 400px 0px';

class ProductRecommendations extends Component {
  /** @type {IntersectionObserver | null} */
  #io = null;
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** @type {boolean} */
  #loaded = false;

  onConnect() {
    // If the section already contains server-rendered content (design mode
    // preview or cached render), skip fetch entirely.
    if (this.dataset.recommendationsPerformed === 'true') {
      this.#loaded = true;
      return;
    }

    this.#io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#io?.disconnect();
            this.#io = null;
            this.#load();
            break;
          }
        }
      },
      { rootMargin: ROOT_MARGIN }
    );
    this.#io.observe(this);
  }

  onDisconnect() {
    this.#io?.disconnect();
    this.#io = null;
    this.#activeFetch?.abort();
    this.#activeFetch = null;
  }

  async #load() {
    if (this.#loaded) return;
    const productId = this.dataset.productId;
    const sectionId = this.dataset.sectionId;
    const intent = this.dataset.intent || 'related';
    const limit = this.dataset.limit || '4';

    if (!productId || !sectionId) {
      this.#fail('missing data-product-id or data-section-id');
      return;
    }

    const url =
      `${window.Shopify?.routes?.root || '/'}recommendations/products` +
      `?section_id=${encodeURIComponent(sectionId)}` +
      `&product_id=${encodeURIComponent(productId)}` +
      `&intent=${encodeURIComponent(intent)}` +
      `&limit=${encodeURIComponent(limit)}`;

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const res = await fetch(url, { signal: this.#activeFetch.signal });
      if (!res.ok) {
        this.#fail(`HTTP ${res.status}`);
        return;
      }
      const text = await res.text();
      if (!text.trim().length) {
        this.#hide();
        return;
      }
      this.#injectResponse(text);
      this.#loaded = true;
      this.dataset.recommendationsPerformed = 'true';
      this.emit('product-recommendations:loaded');
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      this.#fail(err instanceof Error ? err.message : String(err));
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Parse the fetched HTML (server-rendered section) and move its meaningful
   * children into this element. Uses DOMParser + adoptNode to avoid re-running
   * inline scripts and to keep DOM state clean.
   * @param {string} html
   */
  #injectResponse(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');

    // Find the matching <product-recommendations> node by id (if present),
    // otherwise fall back to the first one in the response.
    const selector = `#${CSS.escape(this.id)}`;
    let source = this.id ? doc.querySelector(selector) : null;
    if (!source) {
      source = doc.querySelector('product-recommendations');
    }
    if (!source) {
      this.#hide();
      return;
    }

    // If the server rendered nothing meaningful inside, hide the section.
    const hasProducts = source.querySelector('[data-product-card], .product-card, .card, .product-recommendations__item');
    const hasAnyContent = source.children.length > 0;
    if (!hasProducts && !hasAnyContent) {
      this.#hide();
      return;
    }

    // Adopt all children of the source into our document, then swap in place.
    const fragment = document.createDocumentFragment();
    for (const child of Array.from(source.childNodes)) {
      fragment.appendChild(document.adoptNode(child));
    }
    this.replaceChildren(fragment);
  }

  #hide() {
    this.hidden = true;
    this.classList.add('product-recommendations--empty');
  }

  /**
   * @param {string} message
   */
  #fail(message) {
    // Fail silently in the UI — log for debug.
    console.warn('[product-recommendations]', message);
    this.#hide();
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
